<!-- src/apps/secret/components/form/DeliveryOptionsFields.vue -->

<script setup lang="ts">
  /**
   * Eightpoint: delivery options for the create-secret forms.
   *
   * - Recipients: several addresses, shown as chips. Each recipient gets
   *   their own link (the backend creates one secret per recipient).
   * - Views allowed: 1-10 (default 1, today's single view).
   * - Require email verification: only with at least one recipient.
   *
   * v-model:recipient holds the addresses as one comma-separated string so
   * the parent form keeps its existing string field.
   */
  import { useI18n } from 'vue-i18n';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import {
    MAX_RECIPIENTS,
    MAX_VIEWS_LIMIT,
    parseRecipientList,
  } from '@/schemas/shapes/v3/delivery-options';
  import { computed, ref, watch } from 'vue';
  import { z } from 'zod';

  import { FIELD_ERROR, FIELD_SHELL } from './fieldStyles';

  export interface Props {
    recipient?: string;
    maxViews?: number;
    verifyRecipient?: boolean;
    showRecipient?: boolean;
    disabled?: boolean;
    cornerClass?: string;
    recipientError?: string;
    /** id/test id of the address input, kept stable for existing tests and labels */
    recipientInputId?: string;
    recipientTestId?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    recipient: '',
    maxViews: 1,
    verifyRecipient: false,
    showRecipient: true,
    disabled: false,
    cornerClass: 'rounded-lg',
    recipientError: '',
    recipientInputId: 'delivery-recipient',
    recipientTestId: 'secret-recipient-input',
  });

  const emit = defineEmits<{
    (e: 'update:recipient', value: string): void;
    (e: 'update:maxViews', value: number): void;
    (e: 'update:verifyRecipient', value: boolean): void;
  }>();

  const { t } = useI18n();

  const emailSchema = z.email();
  const isValidEmail = (value: string) => emailSchema.safeParse(value).success;

  const chips = ref<string[]>(parseRecipientList(props.recipient));
  const draft = ref('');
  const viewOptions = Array.from({ length: MAX_VIEWS_LIMIT }, (_, i) => i + 1);

  const combined = computed(() => {
    const all = [...chips.value];
    const pending = draft.value.trim();
    if (pending) all.push(pending);
    return all.join(', ');
  });

  const hasRecipients = computed(() => parseRecipientList(combined.value).length > 0);
  const tooMany = computed(() => parseRecipientList(combined.value).length > MAX_RECIPIENTS);

  const viewsId = computed(() => `${props.recipientInputId}-views`);
  const verifyId = computed(() => `${props.recipientInputId}-verify`);
  const hintId = computed(() => `${props.recipientInputId}-hint`);

  // The parent resets the form after a send: follow it.
  watch(
    () => props.recipient,
    (value) => {
      if ((value ?? '') === combined.value) return;
      chips.value = parseRecipientList(value);
      draft.value = '';
    }
  );

  watch(hasRecipients, (present) => {
    if (!present && props.verifyRecipient) emit('update:verifyRecipient', false);
  });

  const sync = () => emit('update:recipient', combined.value);

  const addChips = (values: string[]) => {
    const next = [...chips.value];
    for (const value of parseRecipientList(values)) {
      if (!next.includes(value)) next.push(value);
    }
    chips.value = next;
  };

  const commitDraft = () => {
    if (!draft.value.trim()) return false;
    addChips([draft.value]);
    draft.value = '';
    sync();
    return true;
  };

  const onInput = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;
    // Typing or pasting a separator turns everything before it into chips.
    if (/[,;\s]/.test(value)) {
      const parts = value.split(/[,;\s]+/);
      const rest = parts.pop() ?? '';
      addChips(parts);
      draft.value = rest;
    } else {
      draft.value = value;
    }
    sync();
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
      // Enter adds the address instead of submitting the form.
      if (commitDraft()) event.preventDefault();
    } else if (event.key === 'Backspace' && !draft.value && chips.value.length) {
      chips.value = chips.value.slice(0, -1);
      sync();
    }
  };

  const removeChip = (value: string) => {
    chips.value = chips.value.filter((chip) => chip !== value);
    sync();
  };

  const onViewsChange = (event: Event) => {
    emit('update:maxViews', Number((event.target as HTMLSelectElement).value));
  };

  const onVerifyChange = (event: Event) => {
    emit('update:verifyRecipient', (event.target as HTMLInputElement).checked);
  };
</script>

<template>
  <div
    class="space-y-4"
    data-testid="delivery-options">
    <!-- Recipients -->
    <div v-if="showRecipient">
      <label
        :for="recipientInputId"
        class="mb-1 block font-brand text-sm text-gray-600 dark:text-gray-300">
        {{ t('web.secrets.delivery.recipients_label') }}
      </label>
      <!-- prettier-ignore-attribute class -->
      <div
        :class="[cornerClass, FIELD_SHELL, recipientError || tooMany ? FIELD_ERROR : '']"
        class="flex flex-wrap items-center gap-1.5 px-2 py-1.5">
        <span
          v-for="chip in chips"
          :key="chip"
          :class="isValidEmail(chip)
            ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200'
            : 'bg-red-50 text-red-700 ring-1 ring-red-300 dark:bg-red-900/40 dark:text-red-200'"
          class="inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-0.5 text-sm"
          data-testid="recipient-chip">
          <span class="truncate">{{ chip }}</span>
          <button
            type="button"
            class="rounded-full p-0.5 hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-brand-500"
            :aria-label="t('web.secrets.delivery.remove_recipient', { email: chip })"
            :disabled="disabled"
            @click="removeChip(chip)">
            <OIcon
              collection="heroicons"
              name="x-mark"
              class="size-3.5"
              aria-hidden="true" />
          </button>
        </span>
        <input
          :id="recipientInputId"
          :value="draft"
          type="email"
          name="recipient[]"
          autocomplete="email"
          multiple
          :disabled="disabled"
          :placeholder="chips.length ? '' : t('web.COMMON.email_placeholder')"
          :aria-describedby="hintId"
          :aria-invalid="!!recipientError || tooMany"
          class="min-w-[12rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-gray-900
            placeholder:text-gray-400 focus:outline-none focus:ring-0
            dark:text-white dark:placeholder:text-gray-500"
          :data-testid="recipientTestId"
          @input="onInput"
          @keydown="onKeydown"
          @blur="commitDraft" />
      </div>
      <p
        :id="hintId"
        class="mt-1 text-xs"
        :class="recipientError || tooMany ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'">
        <template v-if="recipientError">{{ recipientError }}</template>
        <template v-else-if="tooMany">
          {{ t('web.secrets.delivery.too_many_recipients', { max: MAX_RECIPIENTS }) }}
        </template>
        <template v-else>{{ t('web.secrets.delivery.recipients_hint') }}</template>
      </p>
    </div>

    <div class="grid gap-4 sm:grid-cols-2 sm:items-start">
      <!-- Views allowed -->
      <div>
        <label
          :for="viewsId"
          class="mb-1 block font-brand text-sm text-gray-600 dark:text-gray-300">
          {{ t('web.secrets.delivery.views_label') }}
        </label>
        <select
          :id="viewsId"
          :value="maxViews"
          name="max_views"
          :disabled="disabled"
          :class="[cornerClass, FIELD_SHELL]"
          class="appearance-none py-2.5 pl-4 pr-10 text-gray-700"
          data-testid="secret-max-views-select"
          @change="onViewsChange">
          <option
            v-for="count in viewOptions"
            :key="count"
            :value="count">
            {{ count === 1 ? t('web.secrets.delivery.views_one') : t('web.secrets.delivery.views_many', { count }) }}
          </option>
        </select>
      </div>

      <!-- Require email verification -->
      <div
        v-if="showRecipient"
        class="sm:pt-7">
        <div class="flex items-start gap-2">
          <input
            :id="verifyId"
            type="checkbox"
            name="verify_recipient"
            :checked="verifyRecipient && hasRecipients"
            :disabled="disabled || !hasRecipients"
            class="mt-0.5 size-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500
              disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
            data-testid="secret-verify-recipient-checkbox"
            @change="onVerifyChange" />
          <label
            :for="verifyId"
            class="text-sm text-gray-700 dark:text-gray-300"
            :class="{ 'opacity-60': !hasRecipients }">
            {{ t('web.secrets.delivery.verify_label') }}
            <span class="block text-xs text-gray-500 dark:text-gray-400">
              {{ hasRecipients ? t('web.secrets.delivery.verify_hint') : t('web.secrets.delivery.verify_needs_recipient') }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
